import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../AuthContext';
import {
  BookOpen, Users, Star, ArrowRight, Sparkles, Video, FileText,
  MessageCircle, TrendingUp, CalendarDays, Trophy, ShieldCheck, CirclePlay,
  Search, BadgeCheck, Lock, Smartphone, Languages, GraduationCap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Logo, { LogoPicture } from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import AnimatedCounter from '../components/AnimatedCounter';
import ThemeToggle from '../components/ThemeToggle';
import { Squiggle, Sparkle4, HeroBlob } from '../components/BrandDecor';
import useReveal from '../hooks/useReveal';
import '../styles/landing-motion.css';
import HowItWorks from '../components/HowItWorks';
import LessonDemo from '../components/LessonDemo';
import BuiltAroundYou from '../components/BuiltAroundYou';
import axios from 'axios';
import { toast } from 'sonner';
import { dashboardPath as dashboardPathFor } from '../utils/dashboard';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState({ approved_tutors: 0, total_students: 0, completed_lessons: 0, avg_rating: 0 });
  const pageRef = useRef(null);
  const progressRef = useRef(null);
  const heroRef = useRef(null);
  const spotRef = useRef(null);
  const tiltRef = useRef(null);
  useReveal(pageRef);

  // Pause every animated section while it is off-screen (less work while the
  // page loads, and kinder to phone batteries). SVG timelines pause too.
  useEffect(() => {
    const root = pageRef.current;
    if (!root || !('IntersectionObserver' in window)) return undefined;
    const zones = root.querySelectorAll('[data-motion-zone]');
    const io = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        target.classList.toggle('motion-off', !isIntersecting);
        target.querySelectorAll('svg').forEach((svg) => {
          try { if (isIntersecting) svg.unpauseAnimations(); else svg.pauseAnimations(); } catch { /* not an SVG timeline */ }
        });
      });
    }, { rootMargin: '120px 0px' });
    zones.forEach((z) => { z.classList.add('motion-off'); io.observe(z); });
    return () => io.disconnect();
  }, []);

  // Cards marked data-tilt lean toward the pointer (desktop, motion allowed).
  useEffect(() => {
    const root = pageRef.current;
    if (!root || !window.matchMedia?.('(pointer: fine)').matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let card = null; let frame = 0; let last = null;
    const reset = (el) => { if (el) el.style.transform = ''; };
    const onMove = (e) => {
      last = e;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const el = last.target.closest?.('[data-tilt]');
        if (el !== card) { reset(card); card = el; }
        if (!el) return;
        const r = el.getBoundingClientRect();
        const x = (last.clientX - r.left) / r.width - 0.5; const y = (last.clientY - r.top) / r.height - 0.5;
        el.style.transform = `perspective(900px) rotateX(${(-y * 6).toFixed(2)}deg) rotateY(${(x * 8).toFixed(2)}deg) translateY(-4px)`;
      });
    };
    const onLeave = () => { reset(card); card = null; };
    root.addEventListener('pointermove', onMove, { passive: true });
    root.addEventListener('pointerleave', onLeave);
    return () => { root.removeEventListener('pointermove', onMove); root.removeEventListener('pointerleave', onLeave); if (frame) cancelAnimationFrame(frame); };
  }, []);

  // Desktop only: a soft spotlight follows the pointer and the logo tilts toward it.
  const motionOk = typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(pointer: fine)').matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pointerFrame = useRef(0);
  const onHeroPointer = (e) => {
    if (!motionOk || pointerFrame.current) return;
    const { clientX, clientY } = e;
    pointerFrame.current = requestAnimationFrame(() => {
      pointerFrame.current = 0;
      const r = heroRef.current?.getBoundingClientRect();
      if (!r) return;
      const x = (clientX - r.left) / r.width; const y = (clientY - r.top) / r.height;
      if (spotRef.current) {
        spotRef.current.style.setProperty('--mx', `${x * 100}%`);
        spotRef.current.style.setProperty('--my', `${y * 100}%`);
        spotRef.current.classList.add('on');
      }
      const logo = tiltRef.current;
      if (logo) {
        logo.style.setProperty('--rx', `${(x - 0.5) * 14}deg`);
        logo.style.setProperty('--ry', `${(0.5 - y) * 10}deg`);
      }
    });
  };
  const onHeroLeave = () => {
    spotRef.current?.classList.remove('on');
    const logo = tiltRef.current;
    if (logo) { logo.style.setProperty('--rx', '0deg'); logo.style.setProperty('--ry', '0deg'); }
  };

  // Thin bar at the top that fills as you scroll (one rAF per scroll burst).
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${max > 0 ? Math.min(1, window.scrollY / max) : 0})`;
      // Hero parallax only matters near the top of the page.
      if (window.scrollY < 1400) heroRef.current?.style.setProperty('--scroll', String(Math.round(window.scrollY)));
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => { window.removeEventListener('scroll', onScroll); if (frame) cancelAnimationFrame(frame); };
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('bge_logged_out')) {
      sessionStorage.removeItem('bge_logged_out');
      // Wait a tick so the <Toaster> (mounted after this page) is listening.
      setTimeout(() => toast.success('Logged out successfully'), 0);
    }
  }, []);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/stats`;
    axios.get(url).then((r) => setStats(r.data || {})).catch(() => {});
  }, []);

  const dashboardPath = user ? dashboardPathFor(user.role) : null;

  const handleGetStarted = () => {
    navigate(dashboardPath || '/auth');
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const navLinks = [
    { label: 'Home', onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' }) },
    { label: 'Tutors', onClick: () => navigate('/tutors') },
    { label: 'Features', onClick: () => scrollTo('features') },
    { label: 'How it works', onClick: () => scrollTo('how-it-works') },
    { label: 'Plans', onClick: () => navigate('/plans') }
  ];

  const heroStats = [
    { icon: GraduationCap, value: stats.approved_tutors, label: 'Expert tutors', testid: 'stat-tutors' },
    { icon: Users, value: stats.total_students, label: 'Active students', testid: 'stat-students' },
    { icon: Video, value: stats.completed_lessons, label: 'Lessons completed', testid: 'stat-lessons' }
  ];

  // Real platform guarantees — shown where the reference design has a logo strip.
  const trustItems = [
    { icon: BadgeCheck, label: 'Verified tutors' },
    { icon: Video, label: 'Live HD video lessons' },
    { icon: Lock, label: 'Secure payments' },
    { icon: Smartphone, label: 'Learn on any device' },
    { icon: Languages, label: 'English · Español · Filipino' }
  ];

  const journey = [
    { icon: CalendarDays, title: 'Book a class', text: 'Pick a teacher and a time that suits you.' },
    { icon: Video, title: 'Meet your teacher', text: 'A live 1-on-1 video lesson in your browser.' },
    { icon: MessageCircle, title: 'Practice speaking', text: 'Real conversations with friendly corrections.' },
    { icon: Trophy, title: 'Speak with confidence', text: 'See your progress grow, lesson by lesson.' },
  ];

  const features = [
    { icon: BookOpen, title: t('landing.feature_expert_title'), description: t('landing.feature_expert_desc') },
    { icon: Users, title: t('landing.feature_personal_title'), description: t('landing.feature_personal_desc') },
    { icon: CalendarDays, title: t('landing.feature_flexible_title'), description: t('landing.feature_flexible_desc') },
    { icon: Video, title: 'Live Video Lessons', description: 'Face-to-face classes right in your browser.' },
    { icon: FileText, title: 'Lesson Materials', description: 'Download worksheets and resources from your tutor.' },
    { icon: TrendingUp, title: 'Progress Tracking', description: 'See your lessons, notes, and growth over time.' }
  ];

  const steps = [
    { icon: Search, title: t('landing.step_1'), sub: 'Filter by schedule & rating' },
    { icon: BadgeCheck, title: t('landing.step_2'), sub: 'Watch trial videos first' },
    { icon: CalendarDays, title: t('landing.step_3'), sub: 'Pick a time that suits you' },
    { icon: MessageCircle, title: 'Keep in touch', sub: 'Chat & notes after class' }
  ];

  // Decorative avatar stack for the hero trust row — purely visual social proof.
  const trustAvatars = [
    { initials: 'AK', color: 'bg-primary' },
    { initials: 'MJ', color: 'bg-gold-500' },
    { initials: 'SR', color: 'bg-navy' },
    { initials: 'TL', color: 'bg-primary-400' }
  ];

  return (
    <div ref={pageRef} className="min-h-screen relative">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 liquid-glass-strong">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Wordmark hides on the narrowest phones so the auth buttons fit */}
            <Logo size="default" className="min-w-0 max-[439px]:[&_span]:hidden" />
            <div className="hidden lg:flex items-center gap-7 text-sm font-medium">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  type="button"
                  onClick={link.onClick}
                  className="nav-link text-text-secondary hover:text-primary transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              <LanguageSwitcher className="hidden sm:inline-flex" />
              <ThemeToggle />
              {user ? (
                <Button
                  data-testid="dashboard-btn"
                  onClick={() => navigate(dashboardPath)}
                  className="liquid-pill-primary liquid-sheen rounded-xl text-sm sm:text-base border-0"
                >
                  {t('nav.dashboard')}
                </Button>
              ) : (
                <>
                  <Button data-testid="login-btn" variant="ghost" onClick={() => navigate('/auth')} className="rounded-xl text-sm sm:text-base px-3 sm:px-4">
                    {t('nav.login')}
                  </Button>
                  <Button data-testid="signup-btn" onClick={() => navigate('/auth?mode=signup')} className="liquid-pill-primary liquid-sheen rounded-xl text-sm sm:text-base px-3 sm:px-5 border-0">
                    {t('nav.signup')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div ref={progressRef} className="scroll-progress" aria-hidden="true" />

      {/* Hero Section */}
      <section ref={heroRef} onPointerMove={onHeroPointer} onPointerLeave={onHeroLeave} className="pt-28 sm:pt-36 pb-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div ref={spotRef} className="hero-spotlight" aria-hidden="true" />
        <div className="dot-grid" aria-hidden="true" />
        <div className="parallax-slow absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="drift-orb drift-a w-72 h-72 bg-primary/40 -top-10 -left-16" />
        </div>
        <div className="parallax-fast absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="drift-orb drift-b w-80 h-80 bg-gold-400/30 top-1/3 right-0" />
        </div>

        <div className="max-w-[1600px] mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            <div>
              <div
                className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-white/60 dark:bg-white/5 text-primary dark:text-primary-300 text-[11px] sm:text-xs font-semibold uppercase tracking-wider px-4 py-1.5 mb-6 stagger-in"
                data-testid="hero-eyebrow"
              >
                <Sparkles className="w-3.5 h-3.5 text-gold-500" />
                <span>Live 1-on-1 lessons ·</span>
                <span className="flip-words text-gold-600 dark:text-gold-300" data-testid="hero-flip-words">
                  <span>
                    <span>Conversation</span><span>Pronunciation</span><span>Grammar</span><span>Business English</span><span aria-hidden="true">Conversation</span>
                  </span>
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.08] mb-6 stagger-in stagger-in-delay-1">
                <span className="block text-navy dark:text-white">
                  <span className="word-rise" style={{ animationDelay: '0.1s' }}>Learn.</span>{' '}
                  <span className="word-rise" style={{ animationDelay: '0.28s' }}>Speak.</span>
                </span>
                <span className="block bg-gradient-to-r from-primary via-primary-400 to-primary-700 dark:from-primary-300 dark:via-gold-300 dark:to-primary-200 bg-clip-text text-transparent pb-1 animate-gradient-text">
                  {t('landing.hero_title')}
                </span>
              </h1>
              <p className="text-lg text-text-secondary mb-8 max-w-xl stagger-in stagger-in-delay-2">
                {t('landing.hero_subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 stagger-in stagger-in-delay-3">
                <div className="burst flex flex-col sm:inline-flex">
                {[0, 45, 90, 135, 180, 225, 270, 315].map((a, k) => <span key={a} className="spark" style={{ '--a': `${a}deg`, '--d': `${k % 2 ? 95 : 125}px`, animationDelay: `${(k % 3) * 0.04}s` }} aria-hidden="true" />)}
                <Button
                  data-testid="get-started-btn"
                  onClick={handleGetStarted}
                  size="lg"
                  className="liquid-pill-primary btn-shine rounded-xl border-0 px-7 py-6 text-base"
                >
                  {t('landing.get_started')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                </div>
                <button
                  type="button"
                  data-testid="browse-tutors-btn"
                  onClick={() => navigate('/tutors')}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 text-base font-semibold text-primary dark:text-primary-300 hover:text-primary-800 dark:hover:text-white transition-colors"
                >
                  <CirclePlay className="w-5 h-5" />
                  {t('landing.browse_tutors')}
                </button>
              </div>

              {/* Trust row — avatar stack + rating for instant credibility */}
              <div className="flex items-center gap-4 mt-10 stagger-in stagger-in-delay-4" data-testid="hero-trust-row">
                <div className="flex -space-x-3">
                  {trustAvatars.map((a, i) => (
                    <div
                      key={i}
                      className={`w-10 h-10 rounded-full ${a.color} border-2 border-white dark:border-slate-900 flex items-center justify-center text-white text-[11px] font-semibold shadow-sm`}
                    >
                      {a.initials}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-0.5">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-gold-400 text-gold-400" />
                    ))}
                    <span className="ml-1.5 text-sm font-semibold text-text-primary">{stats.avg_rating || 4.9}</span>
                  </div>
                  <p className="text-sm text-text-secondary">
                    Join <span className="font-semibold text-text-primary">{stats.total_students || '500'}+</span> students learning with Bridgeway
                  </p>
                </div>
              </div>
            </div>

            {/* Hero visual — the brand shield on a soft blue blob, with floating stat cards */}
            <div className="relative stagger-in stagger-in-delay-2">
              <div className="relative mx-auto max-w-xl lg:mr-0 flex items-center justify-center min-h-[360px] sm:min-h-[460px]">
                <HeroBlob className="animate-blob" />
                <div className="absolute w-56 h-56 sm:w-72 sm:h-72 rounded-full bg-gold-300/50 blur-3xl" aria-hidden="true" />
                <Squiggle className="draw-in absolute top-2 left-0 sm:left-4 w-24 sm:w-28 text-navy/70 dark:text-white/50" />
                <Sparkle4 className="animate-twinkle absolute bottom-6 right-[30%] w-7 h-7 text-navy/70 dark:text-gold-300" />
                <Sparkle4 className="animate-twinkle absolute top-10 right-[36%] w-4 h-4 text-gold-500 [animation-delay:1.4s]" />
                {/* Paper plane looping above the logo, with a faint dotted trail */}
                <div className="hidden lg:block absolute z-30 -top-6 left-[2%] w-[420px] h-[130px] pointer-events-none" aria-hidden="true">
                  <svg className="w-full h-full overflow-visible" viewBox="0 0 420 130" fill="none">
                    <path id="planePath" className="plane-trail text-primary/40 dark:text-gold-300/40" d="M0 95 C 60 40, 120 50, 200 90 S 330 40, 400 15"
                      stroke="currentColor" strokeWidth="2" strokeDasharray="2 7" strokeLinecap="round" />
                    {/* The plane follows the dotted trail and turns with it (SVG motion path). */}
                    <g className="plane-svg" opacity="0">
                      <g transform="scale(1.25) rotate(45) translate(-12 -12)">
                        <path d="M22 2 11 13" stroke="#facc15" strokeWidth="2" strokeLinecap="round" />
                        <path d="M22 2 15 22l-4-9-9-4 20-7Z" fill="#facc15" stroke="#b45309" strokeWidth="1.2" strokeLinejoin="round" />
                      </g>
                      <animateMotion dur="11s" repeatCount="indefinite" rotate="auto" keyPoints="0;1;1" keyTimes="0;0.6;1" calcMode="linear">
                        <mpath href="#planePath" />
                      </animateMotion>
                      <animate attributeName="opacity" dur="11s" repeatCount="indefinite" values="0;1;1;0;0" keyTimes="0;0.05;0.52;0.6;1" />
                    </g>
                  </svg>
                </div>

                {/* Chat bubbles in different languages pop up one after another */}
                {[
                  ['Hello! 👋', 'top-[6%] left-[4%]', 0],
                  ['Kumusta? 😊', 'top-[18%] right-[38%] sm:right-[44%]', 2],
                  ['Nice to meet you!', 'bottom-[22%] left-[0%]', 4],
                  ['¡Hola! ¿Cómo estás?', 'top-[2%] right-[30%] hidden sm:block', 6],
                  ['Let\'s practice! 🎯', 'bottom-[8%] left-[18%]', 8],
                  ['How was your day?', 'top-[40%] left-[-2%] hidden sm:block', 10],
                ].map(([text, pos, delay]) => (
                  <span
                    key={text}
                    className={`chat-pop ${pos} z-20 rounded-2xl rounded-bl-sm bg-white dark:bg-slate-800 text-navy dark:text-white text-xs sm:text-sm font-semibold px-3 py-2 shadow-card-hover border border-primary/10 dark:border-white/10`}
                    style={{ animationDelay: `${delay}s` }}
                    aria-hidden="true"
                  >
                    {text}
                  </span>
                ))}
                <div
                  className="absolute bottom-[2%] right-[34%] sm:right-[46%] z-20 inline-flex items-center gap-2 rounded-full bg-navy text-white text-[11px] sm:text-xs font-semibold pl-3 pr-3.5 py-1.5 shadow-card-hover"
                  style={{ backgroundColor: '#001C50' }}
                  data-testid="hero-live-badge"
                >
                  <span className="live-dot" aria-hidden="true" />
                  Live class
                  <span className="inline-flex items-center gap-[3px] text-gold-300" aria-hidden="true">
                    <span className="voice-bar" /><span className="voice-bar" /><span className="voice-bar" /><span className="voice-bar" /><span className="voice-bar" />
                  </span>
                </div>
                <div className="relative sm:-translate-x-20">
                  <div className="orbit" aria-hidden="true">
                    {[[BookOpen, '50%', '0%'], [Video, '93%', '75%'], [MessageCircle, '7%', '75%']].map(([Icon, left, top]) => (
                      <span key={left} className="orbit-item" style={{ left, top }}>
                        <span className="w-full h-full rounded-full bg-white dark:bg-slate-800 shadow-card-hover border border-primary/15 dark:border-white/10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-primary dark:text-gold-300" />
                        </span>
                      </span>
                    ))}
                  </div>
                  <div ref={tiltRef} className="tilt">
                  <LogoPicture
                    large
                    className="w-56 sm:w-72 h-auto drop-shadow-[0_24px_40px_rgba(0,28,80,0.35)] animate-float"
                    data-testid="hero-logo"
                    fetchPriority="high"
                  />
                  </div>
                </div>

                {/* Floating stat cards (stacked right on desktop) */}
                <div className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 flex-col gap-4">
                  {heroStats.map((s, i) => {
                    const Icon = s.icon;
                    return (
                      <div
                        key={s.testid}
                        data-testid={s.testid}
                        className="rounded-2xl px-4 py-3.5 w-44 bg-white/95 dark:bg-slate-900/90 border border-primary/10 dark:border-white/10 shadow-card-hover backdrop-blur-md animate-float-slow"
                        style={{ animationDelay: `${i * -2.6}s` }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full liquid-icon-chip flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-primary dark:text-primary-300" />
                          </div>
                          <div>
                            <AnimatedCounter value={s.value} suffix="+" className="text-2xl font-heading font-bold text-navy dark:text-white leading-none" />
                            <p className="text-xs text-text-secondary mt-1">{s.label}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Mobile: stat cards as a row under the visual */}
              <div className="grid grid-cols-3 gap-3 sm:hidden mt-4">
                {heroStats.map((s) => (
                  <div key={s.testid} className="liquid-glass rounded-2xl px-2 py-3 text-center">
                    <AnimatedCounter value={s.value} suffix="+" className="text-xl font-heading font-bold text-navy dark:text-white" />
                    <p className="text-[11px] text-text-secondary mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Trust strip */}
          <div className="reveal mt-12 liquid-glass rounded-2xl px-6 py-6 bg-white/70 dark:bg-transparent" data-testid="trust-strip">
            <p className="text-center text-sm text-text-secondary mb-5">
              Everything you need for safe, effective online English learning
            </p>
            <div className="ticker" data-testid="trust-ticker">
              <div className="ticker-track">
                {[0, 1].map((copy) => (
                  <div key={copy} className="flex items-center gap-x-12 pr-12" aria-hidden={copy === 1 ? 'true' : undefined}>
                    {trustItems.map(({ icon: Icon, label }) => (
                      <div key={label} className="flex items-center gap-2 text-navy dark:text-white font-semibold text-sm sm:text-base whitespace-nowrap">
                        <Icon className="w-5 h-5 text-gold-500" />
                        {label}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features — six icon columns with dividers */}
      <section id="features" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 relative scroll-mt-20" data-motion-zone>
        <div className="max-w-[1600px] mx-auto">
          <div className="reveal mb-12 max-w-2xl">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy dark:text-white mb-3 leading-tight">
              {t('landing.features_title')}
            </h2>
            <span className="draw-line block h-1 w-24 rounded-full bg-gradient-to-r from-primary to-gold-400 mb-4" aria-hidden="true" />
            <p className="text-lg text-text-secondary">
              {t('landing.features_subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-10 lg:divide-x divide-primary/10">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  data-testid={`feature-card-${index}`}
                  className="reveal lift group text-center px-4"
                  style={{ transitionDelay: `${index * 90}ms` }}
                >
                  <div className="w-16 h-16 rounded-full liquid-icon-chip flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:bg-gold-100 transition-all duration-300">
                    <span className="icon-pop inline-flex" style={{ animationDelay: `${index * 110 + 150}ms` }}>
                      <Icon className="w-7 h-7 text-primary dark:text-primary-300" strokeWidth={1.75} />
                    </span>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-navy dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Big outlined subjects sliding in opposite directions */}
      <section className="py-6 overflow-hidden select-none" aria-hidden="true" data-testid="subject-band" data-motion-zone>
        {[false, true].map((rev) => (
          <div key={String(rev)} className={`band-track ${rev ? 'reverse' : ''} py-1`}>
            {[0, 1].map((copy) => (
              <div key={copy} className="flex items-center gap-10 pr-10 text-5xl sm:text-7xl font-heading font-black uppercase tracking-tight whitespace-nowrap">
                {(rev ? ['Vocabulary', 'Listening', 'Business English', 'Speaking', 'Grammar'] : ['Conversation', 'Grammar', 'Pronunciation', 'Business English', 'Vocabulary']).map((w, i) => (
                  <React.Fragment key={w}>
                    <span className={i % 2 ? 'text-navy/80 dark:text-white/80' : 'outline-text'}>{w}</span>
                    <span className="text-gold-400 text-3xl sm:text-5xl">✦</span>
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        ))}
      </section>

      {/* Learning journey — the path draws itself as you scroll */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8" data-testid="journey" data-motion-zone>
        <div className="reveal max-w-[1440px] mx-auto">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy dark:text-white mb-2 text-center">Your path to confident English</h2>
          <span className="draw-line block h-1 w-20 mx-auto rounded-full bg-gradient-to-r from-primary to-gold-400 mb-3" aria-hidden="true" />
          <p className="text-text-secondary text-center mb-10">From your first booking to speaking with confidence, one class at a time.</p>
          <div className="relative">
            <svg className="hidden md:block absolute left-0 right-0 top-0 w-full h-20 pointer-events-none" viewBox="0 0 800 80" preserveAspectRatio="none" aria-hidden="true">
              <defs>
                <linearGradient id="journeyGrad" x1="0" x2="1">
                  <stop offset="0%" stopColor="#1e40af" /><stop offset="60%" stopColor="#3b82f6" /><stop offset="100%" stopColor="#facc15" />
                </linearGradient>
              </defs>
              <path className="journey-path" pathLength="1" d="M100 40 C 160 0, 240 0, 300 40 S 440 80, 500 40 S 640 0, 700 40"
                fill="none" stroke="url(#journeyGrad)" strokeWidth="3" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            </svg>
            <ol className="grid grid-cols-2 md:grid-cols-4 gap-y-8 relative">
              {journey.map(({ icon: Icon, title, text }, i) => (
                <li key={title} className="journey-step text-center px-3" style={{ animationDelay: `${0.35 + i * 0.45}s` }}>
                  <div className="journey-dot w-16 h-16 mx-auto mt-2 mb-4 rounded-full bg-white dark:bg-slate-800 border-2 border-primary/30 dark:border-gold-300/40 shadow-card-hover flex items-center justify-center">
                    <Icon className="w-7 h-7 text-primary dark:text-gold-300" strokeWidth={1.75} />
                  </div>
                  <p className="text-xs font-semibold text-gold-600 dark:text-gold-300 mb-1">Step {i + 1}</p>
                  <h3 className="font-heading font-semibold text-navy dark:text-white mb-1">{title}</h3>
                  <p className="text-sm text-text-secondary">{text}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* A mock lesson that plays itself */}
      <LessonDemo />

      {/* Level meter + time-zone clocks */}
      <BuiltAroundYou />

      {/* How it works — clickable steps drive a live product preview */}
      <HowItWorks title={t('landing.how_title')} steps={steps} />

      {/* CTA band */}
      <section className="py-10 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-motion-zone>
        {[['Hello!', '6%', 0, 'text-4xl'], ['Fluency', '22%', 3.5, 'text-5xl'], ['Grammar', '42%', 7, 'text-3xl'], ['Confidence', '60%', 1.8, 'text-5xl'], ['Vocabulary', '78%', 5.2, 'text-4xl'], ['Kumusta!', '90%', 9, 'text-3xl']].map(([w, left, delay, size]) => (
          <span key={w} className={`float-word ${size}`} style={{ left, animationDelay: `${delay}s` }} aria-hidden="true">{w}</span>
        ))}
        <div className="reveal relative max-w-[1600px] mx-auto liquid-glass liquid-sheen overflow-hidden rounded-[2rem] px-6 sm:px-10 py-10 bg-white/70 dark:bg-transparent">
          <div className="aurora" aria-hidden="true" />
          <div className="relative flex flex-col md:flex-row items-center gap-8">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gold-100 to-primary-100 dark:from-gold-500/20 dark:to-primary/30 flex items-center justify-center flex-shrink-0">
              <Trophy className="w-11 h-11 text-gold-500 animate-float" strokeWidth={1.75} />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy dark:text-white mb-2">
                {t('landing.cta_title')}
              </h2>
              <p className="text-text-secondary">
                {t('landing.cta_subtitle')}
              </p>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="burst flex flex-col sm:inline-flex">
              {[0, 45, 90, 135, 180, 225, 270, 315].map((a, k) => <span key={a} className="spark" style={{ '--a': `${a}deg`, '--d': `${k % 2 ? 95 : 125}px`, animationDelay: `${(k % 3) * 0.04}s` }} aria-hidden="true" />)}
              <Button
                data-testid="cta-get-started-btn"
                onClick={handleGetStarted}
                size="lg"
                className="liquid-pill-primary btn-shine rounded-xl border-0 px-10 py-6 text-base font-semibold"
              >
                {t('landing.cta_button')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              </div>
              <p className="flex items-center gap-1.5 text-xs text-text-secondary">
                <ShieldCheck className="w-4 h-4 text-primary dark:text-primary-300" />
                Verified tutors · Secure checkout
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      {/* Teacher recruitment */}
      <section className="px-4 py-12" data-testid="teach-with-us" data-motion-zone>
        <div className="glow-frame max-w-5xl mx-auto rounded-3xl p-[2px] shadow-card">
        <div className="rounded-[calc(1.5rem-2px)] bg-white dark:bg-slate-900 p-6 sm:p-10 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <p className="text-sm font-semibold text-primary mb-1">Teach with Bridgeway</p>
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary mb-2">Love teaching English? Join our team.</h2>
            <p className="text-text-secondary">Apply online, complete our teacher training, and teach students one-to-one from home.</p>
          </div>
          <Button size="lg" className="rounded-full self-start md:self-auto" onClick={() => navigate('/apply')} data-testid="landing-apply-btn">Apply to teach</Button>
        </div>
        </div>
      </section>

      <footer className="py-14 px-4 sm:px-6 lg:px-8 liquid-glass-footer relative text-white" data-motion-zone>
        <div className="footer-waves" aria-hidden="true">
          <svg className="wave-b" viewBox="0 0 1440 40" preserveAspectRatio="none"><path d="M0 20 Q 180 0 360 20 T 720 20 T 1080 20 T 1440 20 V40 H0Z" fill="rgba(59,130,246,0.35)" /></svg>
          <svg className="wave-a" viewBox="0 0 1440 40" preserveAspectRatio="none"><path d="M0 24 Q 180 6 360 24 T 720 24 T 1080 24 T 1440 24 V40 H0Z" fill="rgba(250,204,21,0.25)" /></svg>
        </div>
        <div className="max-w-[1600px] mx-auto">
          <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-8 pb-8 border-b border-white/10">
            <div className="text-center md:text-left max-w-sm">
              <Logo size="small" className="text-white [&_span]:text-white justify-center md:justify-start" />
              <p className="text-slate-400 text-sm mt-4 leading-relaxed">
                Personalized, live English tutoring with expert tutors — learn at your own pace, on your own schedule.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm">
              <button
                type="button"
                onClick={() => navigate('/tutors')}
                className="text-slate-300 hover:text-gold-300 transition-colors"
                data-testid="footer-link-tutors"
              >
                Browse Tutors
              </button>
              <button
                type="button"
                onClick={() => navigate('/plans')}
                className="text-slate-300 hover:text-gold-300 transition-colors"
                data-testid="footer-link-packages"
              >
                Plans
              </button>
              <button
                type="button"
                onClick={handleGetStarted}
                className="text-slate-300 hover:text-gold-300 transition-colors"
                data-testid="footer-link-get-started"
              >
                Get Started
              </button>
              <button
                type="button"
                onClick={() => navigate('/apply')}
                className="text-slate-300 hover:text-gold-300 transition-colors"
                data-testid="footer-link-apply"
              >
                Teach with us
              </button>
              <button
                type="button"
                onClick={() => navigate('/privacy')}
                className="text-slate-300 hover:text-gold-300 transition-colors"
                data-testid="footer-link-privacy"
              >
                Privacy Policy
              </button>
            </div>
          </div>
          <p className="text-slate-500 text-sm text-center pt-8">{t('landing.footer_rights')}</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
